/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { useScheduleSettings } from '@sportninja/common/hooks/useScheduleSettings'
import { Link } from 'react-router-dom'
import { Table, Avatar, Typography, Input, Spin, Tooltip } from 'antd'
import { Flex } from '../../Layout'
import GameListFilters, {
  SortOrderButton,
} from '../../List/GameList/GameListFilters'
import useGetScheduleFiltering from '../../List/useGetScheduleFiltering'
import Icon from '../../Icon'
import colors from '@sportninja/common/constants/appColors'
import cn from 'classnames'
import ReprocessingSettingsOverlay from 'src/pages/Schedule/ReprocessingSettingsOverlay'
import { getStatTypesBySportId } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import Button from 'src/components/ts-components/Button'

const SCROLL_THRESHOLD = 500

const Statistics = ({
  autoSelectMostRecentComp,
  areFiltersHidden,
  entityType,
  entityId,
  defaultSort,
  refreshKey,
  scheduleReprocessingListener,
  isSoccer = false,
  orgId,
  displaySearch = true,
  sportId,
  statTypes,
}) => {
  // Fetch schedule settings for dynamic columns
  const { scheduleSettings, fetchScheduleSettings, clearScheduleSettings } =
    useScheduleSettings()

  const { stats } = scheduleSettings?.settings ?? {}
  const { player: playerSettings, goalie: goalieSettings } = stats ?? {}

  const playerDisplayedIds = useMemo(
    () => playerSettings?.displayed ?? [],
    [playerSettings]
  )
  const goalieDisplayedIds = useMemo(
    () => goalieSettings?.displayed ?? [],
    [goalieSettings]
  )

  // Index statTypes by abbreviation for quick lookup
  const statTypesByAbbr = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.abbreviation) {
        acc[statType.abbreviation] = statType
      }
      return acc
    }, {})
  }, [statTypes])

  // Create mapping from abbreviation to stat_id for table onChange handler
  const statIdByAbbr = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.abbreviation && statType.id) {
        acc[statType.abbreviation] = statType.id
      }
      return acc
    }, {})
  }, [statTypes])

  // Reverse mapping from stat ID to abbreviation for column highlighting
  const abbrByStatId = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.abbreviation && statType.id) {
        acc[statType.id] = statType.abbreviation
      }
      return acc
    }, {})
  }, [statTypes])

  // Index statTypes by ID for dynamic column generation
  const statTypesById = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.id) {
        acc[statType.id] = statType
      }
      return acc
    }, {})
  }, [statTypes])

  // Compute default sort from settings - with fallback to legacy behavior
  const playerDefaultSort = useMemo(() => {
    const statId = playerSettings?.sort_order?.stat_id
    if (statId && statTypesById[statId]) {
      return statId
    }
    // Fallback to legacy behavior
    const abbr = isSoccer ? 'G' : 'P'
    return statIdByAbbr[abbr] || abbr
  }, [playerSettings, statTypesById, isSoccer, statIdByAbbr])

  const goalieDefaultSort = useMemo(() => {
    const statId = goalieSettings?.sort_order?.stat_id
    if (statId && statTypesById[statId]) {
      return statId
    }
    // Fallback to legacy behavior
    return statIdByAbbr['GAA'] || 'GAA'
  }, [goalieSettings, statTypesById, statIdByAbbr])

  // Get default sort direction for a stat based on is_sort_desc
  const getDefaultDirection = useCallback(
    (statId) => {
      const abbr = abbrByStatId[statId]
      const statType = abbr ? statTypesByAbbr[abbr] : null
      // If is_sort_desc is true, default to desc; otherwise asc
      return statType?.is_sort_desc === true ? 'desc' : 'asc'
    },
    [abbrByStatId, statTypesByAbbr]
  )

  // Get sort directions based on is_sort_desc
  // If is_sort_desc is true, first click should be desc, otherwise asc
  const getSortDirections = useCallback(
    (abbreviation) => {
      const statType = statTypesByAbbr[abbreviation]
      if (statType?.is_sort_desc === true) {
        return ['descend', 'ascend', 'descend']
      }
      return ['ascend', 'descend', 'ascend']
    },
    [statTypesByAbbr]
  )

  // Get sort directions based on stat ID (for dynamic columns)
  const getSortDirectionsById = useCallback(
    (statId) => {
      const statType = statTypesById[statId]
      if (statType?.is_sort_desc === true) {
        return ['descend', 'ascend', 'descend']
      }
      return ['ascend', 'descend', 'ascend']
    },
    [statTypesById]
  )

  // Get tooltip title from statTypes
  const getStatTooltip = useCallback(
    (abbreviation, fallbackTitle) => {
      const statType = statTypesByAbbr[abbreviation]
      if (statType?.name && statType?.description) {
        return (
          <span>
            <span
              css={css`
                font-weight: 600;
              `}
            >
              {statType.name}
            </span>{' '}
            - {statType.description}
          </span>
        )
      }
      return statType?.name || fallbackTitle
    },
    [statTypesByAbbr]
  )

  // Render stat title with tooltip above the label
  const renderStatTitle = useCallback(
    (abbr, label, fallback) => (
      <Tooltip
        title={getStatTooltip(abbr, fallback ?? label)}
        placement='top'
        mouseEnterDelay={0.1}
      >
        <span
          css={css`
            display: block;
            width: 100%;
          `}
        >
          {label}
        </span>
      </Tooltip>
    ),
    [getStatTooltip]
  )

  // Get stat value from item stats array
  const getStatValue = useCallback((itemStats, abbreviation) => {
    if (!itemStats || !Array.isArray(itemStats)) {
      return 0
    }
    const stat = itemStats.find((s) => s.abbr === abbreviation)
    return stat?.value ?? 0
  }, [])

  // Helper function to generate columns from stat IDs
  const generateColumnsFromIds = useCallback(
    (ids, defaultStatId) => {
      if (!ids?.length || !statTypesById) {
        return []
      }

      return ids
        .map((id) => {
          const statType = statTypesById[id]
          if (!statType) {
            return null
          }
          const abbr = statType.abbreviation
          const isDefaultSort = id === defaultStatId
          return {
            title: renderStatTitle(abbr, abbr, statType.name),
            key: abbr,
            sorter: (a, b, sortOrder) => {},
            sortDirections: getSortDirectionsById(id),
            dataIndex: abbr,
            defaultSortOrder: isDefaultSort
              ? getSortDirectionsById(id)[0]
              : undefined,
            showSorterTooltip: false,
          }
        })
        .filter(Boolean)
    },
    [getSortDirectionsById, renderStatTitle, statTypesById]
  )

  const BASE_SLUG = `/${entityType}/${entityId}/stats/players`

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [sortBy, setSortBy] = useState(null)
  const [direction, setDirection] = useState('desc')
  const [items, setItems] = useState([])
  const [slug, setSlug] = useState(BASE_SLUG)
  const [isGlobalFilterEnabled, setIsGlobalFilterEnabled] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isAppending, setIsAppending] = useState(false)

  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchTerm = useDebounce(searchValue, 500)
  const manualSearchRef = useRef(false)
  const settingsAppliedRef = useRef(false)

  // Set initial sortBy when statIdByAbbr becomes available
  useEffect(() => {
    if (sortBy === null && playerDefaultSort) {
      // Use abbreviation for BASE_SLUG, stat ID otherwise
      const defaultAbbr = isSoccer ? 'G' : 'P'
      setSortBy(slug === BASE_SLUG ? defaultAbbr : playerDefaultSort)
    }
  }, [sortBy, playerDefaultSort, slug, isSoccer])

  // Compute the current sort abbreviation for column highlighting
  const currentSortAbbr = useMemo(() => {
    if (!sortBy) return null
    // If sortBy is already an abbreviation, use it directly
    // Otherwise, look up the abbreviation from the stat ID
    return abbrByStatId[sortBy] || sortBy
  }, [sortBy, abbrByStatId])

  const isSchedule = entityType === ENTITY_TYPES.schedule
  const isTeam = entityType === ENTITY_TYPES.team
  const args = {}

  if (isSchedule) {
    args.schedule_id = entityId
  } else if (isTeam) {
    args.team_id = entityId
  }

  const onTeamChange = (teamId) => {
    setTeam(teamId)
    if (teamId) {
      setSlug(`/schedules/${entityId}/stats/team/${teamId}`)
    } else {
      setSlug(BASE_SLUG)
    }
  }

  const onScheduleChange = (scheduleId) => {
    if (scheduleId) {
      setScheduleForSearch(scheduleId)
      setSlug(
        isSchedule
          ? `/schedules/${scheduleId}/stats/players`
          : `/schedules/${scheduleId}/stats/team/${entityId}`
      )
    } else {
      setSlug(BASE_SLUG)
    }
  }

  const onIsGoalie = (isGoalie) => {
    setIsGoalieStats(isGoalie)
  }

  const {
    teams,
    team,
    setTeam,
    filters,
    state,
    setState,
    loaded,
    currentParams,
  } = useGetScheduleFiltering(args, {
    defaultSort: 'desc',
    autoSelectMostRecentComp,
    onTeamChange,
    onScheduleChange,
    onIsGoalie,
  })

  useEffect(() => {
    if (currentParams.sn_division) {
      setSlug(`/schedules/${currentParams.sn_division}/stats/team/${entityId}`)
    } else if (currentParams.sn_schedule) {
      setSlug(`/schedules/${currentParams.sn_schedule}/stats/team/${entityId}`)
    }
  }, [currentParams, entityId, setTeam])

  useEffect(() => {
    if (currentParams.sn_team) {
      setTeam(currentParams.sn_team)
    }
  }, [currentParams, setTeam])

  useEffect(() => {
    setTeam(entityId)
  }, [entityId, setTeam])

  // Determine schedule ID for settings fetch
  const scheduleIdForSettings = useMemo(() => {
    // Priority: division > schedule > entityId (if schedule type)
    if (currentParams?.sn_division) return currentParams.sn_division
    if (currentParams?.sn_schedule) return currentParams.sn_schedule
    if (isSchedule) return entityId
    return null
  }, [
    currentParams?.sn_division,
    currentParams?.sn_schedule,
    isSchedule,
    entityId,
  ])

  // Fetch schedule settings when schedule ID changes, clear when unselected
  useEffect(() => {
    if (scheduleIdForSettings) {
      fetchScheduleSettings(scheduleIdForSettings)
    } else {
      // Clear settings when no schedule is selected (fallback to hardcoded columns)
      clearScheduleSettings()
      settingsAppliedRef.current = false
    }
  }, [scheduleIdForSettings, fetchScheduleSettings, clearScheduleSettings])

  // Reset sort to default when settings are cleared (scheduleIdForSettings becomes null)
  const prevScheduleIdRef = useRef(scheduleIdForSettings)
  useEffect(() => {
    const hadSchedule = prevScheduleIdRef.current !== null
    const noScheduleNow = scheduleIdForSettings === null
    prevScheduleIdRef.current = scheduleIdForSettings

    // Only reset sort when transitioning from having a schedule to no schedule
    if (hadSchedule && noScheduleNow) {
      const defaultPlayerAbbr = isSoccer ? 'G' : 'P'
      const fallbackSortId =
        statIdByAbbr[defaultPlayerAbbr] || defaultPlayerAbbr
      setSortBy(fallbackSortId)
      const statType = statTypesByAbbr[defaultPlayerAbbr]
      setDirection(statType?.is_sort_desc === true ? 'desc' : 'asc')
    }
  }, [scheduleIdForSettings, isSoccer, statIdByAbbr, statTypesByAbbr])

  // Reset settings applied flag when entityId changes
  useEffect(() => {
    settingsAppliedRef.current = false
  }, [entityId, entityType])

  const [scheduleForSearch, setScheduleForSearch] = useState(null)
  const [isGoalieStats, setIsGoalieStats] = useState(false)

  // Apply settings-based sortBy when settings become available
  useEffect(() => {
    const hasPlayerSettings = playerSettings?.sort_order?.stat_id
    const hasGoalieSettings = goalieSettings?.sort_order?.stat_id

    if (
      !settingsAppliedRef.current &&
      (hasPlayerSettings || hasGoalieSettings)
    ) {
      if (!isGoalieStats && hasPlayerSettings) {
        setSortBy(playerDefaultSort)
        setDirection(getDefaultDirection(playerDefaultSort))
      } else if (isGoalieStats && hasGoalieSettings) {
        setSortBy(goalieDefaultSort)
        setDirection(getDefaultDirection(goalieDefaultSort))
      }
      settingsAppliedRef.current = true
    }
  }, [
    playerSettings,
    goalieSettings,
    playerDefaultSort,
    goalieDefaultSort,
    isGoalieStats,
    getDefaultDirection,
  ])

  const getItems = useCallback(
    async (page, overrideSearchTerm, mode = 'reset') => {
      if (!sortBy) {
        return
      }

      const isAppend = mode === 'append'

      if (isAppend) {
        setIsAppending(true)
      } else {
        setLoading(true)
      }

      setError(null)

      try {
        const searchTerm =
          overrideSearchTerm !== undefined
            ? overrideSearchTerm
            : debouncedSearchTerm
        const response = await req(slug, {
          query: {
            page,
            sortBy,
            sort: direction,
            goalie: isGoalieStats ? 1 : 0,
            ...(scheduleForSearch && { division_id: scheduleForSearch }),
            ...(isGlobalFilterEnabled && { global: 1 }),
            ...(searchTerm && { player_name: searchTerm }),
          },
        })

        const nextData = response?.data ?? []
        setItems((prev) => (isAppend ? [...prev, ...nextData] : nextData))

        const pg = response?.meta?.pagination

        const cp = pg?.current_page ?? page ?? 1
        setCurrentPage(Number(cp))

        const per = pg?.per_page ?? nextData.length
        const total = pg?.count ?? 0
        setHasMore(total ? cp * per < total : nextData.length > 0)
      } catch (e) {
        setError(e.message)
      } finally {
        if (isAppend) {
          setIsAppending(false)
        } else {
          setLoading(false)
        }
      }
    },
    [
      sortBy,
      isGoalieStats,
      direction,
      slug,
      scheduleForSearch,
      isGlobalFilterEnabled,
      debouncedSearchTerm,
    ]
  )

  useEffect(() => {
    if (!loaded) {
      return
    }
    if (manualSearchRef.current) {
      manualSearchRef.current = false
      return
    }
    setCurrentPage(1)
    setHasMore(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    getItems(1, undefined, 'reset')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loaded,
    direction,
    slug,
    entityId,
    sortBy,
    isGoalieStats,
    refreshKey,
    scheduleForSearch,
    isGlobalFilterEnabled,
    debouncedSearchTerm,
  ])

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore && !loading && !isAppending) {
      getItems(currentPage + 1, undefined, 'append')
    }
  }, [hasMore, loading, isAppending, currentPage, getItems])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const commonColumns = useMemo(
    () => [
      {
        title: '#',
        key: 'index',
        width: 50,
        align: 'center',
        fixed: 'left',
        render: (_, __, index) => {
          return <Typography.Text>{index + 1}</Typography.Text>
        },
      },
      {
        title: 'Player',
        key: 'player',
        dataIndex: 'player',
        sorter: (a, b, sortOrder) => {},
        showSorterTooltip: false,
        align: 'left',
        render: (item) => {
          return (
            <Link
              to={`${ROUTES.PLAYER_ROOT}/${item?.player?.id}`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                max-width: 270px;
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(item?.player) ||
                  '/images/placeholders/hockey/player.png'
                }
                shape='square'
                icon={<Icon name='user' fontSize={14} />}
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  margin: 0;
                `}
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                `}
              >
                <Typography.Text strong ellipsis>
                  {item?.player?.name_first} {item?.player?.name_last}
                </Typography.Text>
                {item?.player?.player_number ? (
                  <Typography.Text
                    css={css`
                      font-size: 12px;
                      color: ${colors.NEUTRAL_50};
                    `}
                  >
                    #{item?.player?.player_number}
                  </Typography.Text>
                ) : null}
              </div>
            </Link>
          )
        },
        fixed: 'left',
        width: 270,
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
    ],
    []
  )

  const skaterColumns = useMemo(
    () => [
      {
        title: renderStatTitle('GP', 'GP', 'Games Played'),
        key: 'GP',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('GP'),
        dataIndex: 'GP',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('G', 'G', 'Goals'),
        key: 'G',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('G'),
        dataIndex: 'G',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('A', 'A', 'Assists'),
        key: 'A',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('A'),
        dataIndex: 'A',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('P', 'PTS', 'Points'),
        key: 'P',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('P'),
        dataIndex: 'P',
        defaultSortOrder: 'descend',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('PiM', 'PIM', 'Penalty Minutes'),
        key: 'PiM',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('PiM'),
        dataIndex: 'PiM',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('PPG', 'PPG', 'Power Play Goals'),
        key: 'PPG',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('PPG'),
        dataIndex: 'PPG',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('PTS/G', 'PTS/G', 'Average Points Per Game'),
        key: 'PTS/G',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('PTS/G'),
        dataIndex: 'PTS/G',
        showSorterTooltip: false,
      },
    ],
    [getSortDirections, renderStatTitle]
  )

  const goalieColumns = useMemo(
    () => [
      {
        title: renderStatTitle('GP', 'GP', 'Games Played'),
        key: 'GP',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('GP'),
        dataIndex: 'GP',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('W', 'W', 'Wins'),
        key: 'W',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('W'),
        dataIndex: 'W',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('L', 'L', 'Losses'),
        key: 'L',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('L'),
        dataIndex: 'L',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('T', 'T', 'Ties'),
        key: 'T',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('T'),
        dataIndex: 'T',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('OTL', 'OTL', 'Overtime Losses'),
        key: 'OTL',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('OTL'),
        dataIndex: 'OTL',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('GAA', 'GAA', 'Goals Against Average'),
        key: 'GAA',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('GAA'),
        dataIndex: 'GAA',
        defaultSortOrder: getSortDirections('GAA')[0],
        showSorterTooltip: false,
        width: 80,
      },
      {
        title: renderStatTitle('SV%', 'SV%', 'Save Percentage'),
        key: 'SV%',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('SV%'),
        dataIndex: 'SV%',
        showSorterTooltip: false,
        width: 80,
      },
      {
        title: renderStatTitle('SO', 'SO', 'Shutouts'),
        key: 'SO',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('SO'),
        dataIndex: 'SO',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('MP', 'MIN', 'Minutes Played'),
        key: 'MP',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('MP'),
        dataIndex: 'MP',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('PiM', 'PIM', 'Penalty Minutes'),
        key: 'PiM',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('PiM'),
        dataIndex: 'PiM',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('SA', 'SA', 'Shots Against'),
        key: 'SA',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('SA'),
        dataIndex: 'SA',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('SV', 'SV', 'Saves'),
        key: 'SV',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('SV'),
        dataIndex: 'SV',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('GA', 'GA', 'Goals Against'),
        key: 'GA',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('GA'),
        dataIndex: 'GA',
        showSorterTooltip: false,
      },
    ],
    [getSortDirections, renderStatTitle]
  )

  const playerColumns = useMemo(
    () => [
      {
        title: renderStatTitle('MP', 'MP', 'Matches Played'),
        key: 'MP',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('MP'),
        dataIndex: 'MP',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('G', 'G', 'Goals'),
        key: 'G',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('G'),
        defaultSortOrder: 'descend',
        dataIndex: 'G',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('A', 'A', 'Assists'),
        key: 'A',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('A'),
        dataIndex: 'A',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('GFA', 'GFA', 'Goals For Average'),
        key: 'GFA',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('GFA'),
        dataIndex: 'GFA',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('FC', 'FC', 'Fouls Committed'),
        key: 'FC',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('FC'),
        dataIndex: 'FC',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('YC', 'YC', 'Yellow Cards'),
        key: 'YC',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('YC'),
        dataIndex: 'YC',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('RC', 'RC', 'Red Cards'),
        key: 'RC',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('RC'),
        dataIndex: 'RC',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('BC', 'BC', 'Blue Cards'),
        key: 'BC',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('BC'),
        dataIndex: 'BC',
        showSorterTooltip: false,
      },
    ],
    [getSortDirections, renderStatTitle]
  )

  const keeperColumns = useMemo(
    () => [
      {
        title: renderStatTitle('MP', 'MP', 'Matches Played'),
        key: 'MP',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('MP'),
        dataIndex: 'MP',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('GA', 'GA', 'Goals Against'),
        key: 'GA',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('GA'),
        dataIndex: 'GA',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('GAA', 'GAA', 'Goals Against Average'),
        key: 'GAA',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('GAA'),
        dataIndex: 'GAA',
        defaultSortOrder: getSortDirections('GAA')[0],
        showSorterTooltip: false,
        width: 80,
      },
      {
        title: renderStatTitle('SV%', 'SV%', 'Save Percentage'),
        key: 'SV%',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('SV%'),
        dataIndex: 'SV%',
        showSorterTooltip: false,
        width: 80,
      },
      {
        title: renderStatTitle('SV', 'SV', 'Saves'),
        key: 'SV',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('SV'),
        dataIndex: 'SV',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('CS', 'CS', 'Clean Sheets'),
        key: 'CS',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('CS'),
        dataIndex: 'CS',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('W', 'W', 'Wins'),
        key: 'W',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('W'),
        dataIndex: 'W',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('L', 'L', 'Loss'),
        key: 'L',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('L'),
        dataIndex: 'L',
        showSorterTooltip: false,
      },
      {
        title: renderStatTitle('D', 'D', 'Draws'),
        key: 'D',
        sorter: (a, b, sortOrder) => {},
        sortDirections: getSortDirections('D'),
        dataIndex: 'D',
        showSorterTooltip: false,
      },
    ],
    [getSortDirections, renderStatTitle]
  )

  // Dynamic skater columns - use settings when available, fallback to static
  const dynamicSkaterColumns = useMemo(() => {
    if (playerDisplayedIds.length > 0) {
      return generateColumnsFromIds(playerDisplayedIds, playerDefaultSort)
    }
    return skaterColumns
  }, [
    playerDisplayedIds,
    playerDefaultSort,
    generateColumnsFromIds,
    skaterColumns,
  ])

  // Dynamic goalie columns - use settings when available, fallback to static
  const dynamicGoalieColumns = useMemo(() => {
    if (goalieDisplayedIds.length > 0) {
      return generateColumnsFromIds(goalieDisplayedIds, goalieDefaultSort)
    }
    return goalieColumns
  }, [
    goalieDisplayedIds,
    goalieDefaultSort,
    generateColumnsFromIds,
    goalieColumns,
  ])

  // Dynamic player columns (soccer) - use settings when available
  const dynamicPlayerColumns = useMemo(() => {
    if (playerDisplayedIds.length > 0) {
      return generateColumnsFromIds(playerDisplayedIds, playerDefaultSort)
    }
    return playerColumns
  }, [
    playerDisplayedIds,
    playerDefaultSort,
    generateColumnsFromIds,
    playerColumns,
  ])

  // Dynamic keeper columns (soccer goalie) - use settings when available
  const dynamicKeeperColumns = useMemo(() => {
    if (goalieDisplayedIds.length > 0) {
      return generateColumnsFromIds(goalieDisplayedIds, goalieDefaultSort)
    }
    return keeperColumns
  }, [
    goalieDisplayedIds,
    goalieDefaultSort,
    generateColumnsFromIds,
    keeperColumns,
  ])

  // Create union of all displayed abbreviations for dynamic data mapping
  const allDisplayedAbbrs = useMemo(() => {
    const allIds = [
      ...(playerDisplayedIds ?? []),
      ...(goalieDisplayedIds ?? []),
    ]
    const uniqueIds = [...new Set(allIds)]
    return uniqueIds
      .map((id) => statTypesById[id]?.abbreviation)
      .filter(Boolean)
  }, [playerDisplayedIds, goalieDisplayedIds, statTypesById])

  const data = useMemo(
    () => [
      ...items.map((item, index) => {
        const itemStats = item?.stats
        const row = {
          key: index,
          player: item,
          team: item,
          PN: item?.player?.player_number,
          // SKATER DATA
          GP: getStatValue(itemStats, 'GP'),
          G: getStatValue(itemStats, 'G'),
          A: getStatValue(itemStats, 'A'),
          P: getStatValue(itemStats, 'P'),
          PiM: getStatValue(itemStats, 'PiM'),
          PPG: getStatValue(itemStats, 'PPG'),
          'PTS/G': getStatValue(itemStats, 'PTS/G'),
          // GOALIE DATA
          W: getStatValue(itemStats, 'W'),
          L: getStatValue(itemStats, 'L'),
          T: getStatValue(itemStats, 'T'),
          OTL: getStatValue(itemStats, 'OTL'),
          GAA: getStatValue(itemStats, 'GAA'),
          'SV%': getStatValue(itemStats, 'SV%'),
          SO: getStatValue(itemStats, 'SO'),
          MP: getStatValue(itemStats, 'MP'),
          SA: getStatValue(itemStats, 'SA'),
          SV: getStatValue(itemStats, 'SV'),
          GA: getStatValue(itemStats, 'GA'),
          // SPECIFIC SOCCER DATA
          'G/M': getStatValue(itemStats, 'G/M'),
          GCA: getStatValue(itemStats, 'GCA'),
          YC: getStatValue(itemStats, 'YC'),
          FC: getStatValue(itemStats, 'FC'),
          GC: getStatValue(itemStats, 'GC'),
          FG: getStatValue(itemStats, 'FG'),
          RC: getStatValue(itemStats, 'RC'),
          BC: getStatValue(itemStats, 'BC'),
          TP: getStatValue(itemStats, 'TP'),
          CS: getStatValue(itemStats, 'CS'),
          D: getStatValue(itemStats, 'D'),
          Mins: getStatValue(itemStats, 'Mins'),
          GF: getStatValue(itemStats, 'GF'),
          GFA: getStatValue(itemStats, 'GFA'),
        }

        // Dynamically populate all displayed abbreviations
        allDisplayedAbbrs.forEach((abbr) => {
          if (!(abbr in row)) {
            row[abbr] = getStatValue(itemStats, abbr)
          }
        })

        return row
      }),
    ],
    [items, getStatValue, allDisplayedAbbrs]
  )

  const columns = useMemo(() => {
    if (isGoalieStats) {
      return commonColumns.concat(
        isSoccer ? dynamicKeeperColumns : dynamicGoalieColumns
      )
    }
    return commonColumns.concat(
      isSoccer ? dynamicPlayerColumns : dynamicSkaterColumns
    )
  }, [
    isGoalieStats,
    isSoccer,
    commonColumns,
    dynamicGoalieColumns,
    dynamicKeeperColumns,
    dynamicPlayerColumns,
    dynamicSkaterColumns,
  ])

  const renderTable = useCallback(() => {
    return (
      <Table
        columns={columns.map((col) => ({
          ...col,
          sortOrder:
            col.key === currentSortAbbr
              ? direction === 'desc'
                ? 'descend'
                : 'ascend'
              : null,
        }))}
        bordered
        loading={loading}
        size='small'
        scroll={{ x: 1300 }}
        pagination={false}
        dataSource={data}
        onChange={(_pagination, _filters, sorter) => {
          const columnKeyMap = {
            player: 'player.full_name',
          }
          const key = sorter?.columnKey
          // Use abbreviation for BASE_SLUG, stat ID for filtered endpoints
          const mappedKey =
            columnKeyMap[key] ||
            (slug === BASE_SLUG ? key : statIdByAbbr[key] || key)
          setSortBy(mappedKey)
          setDirection(sorter?.order === 'descend' ? 'desc' : 'asc')
        }}
        css={css`
          .ant-table-measure-row {
            display: none !important;
          }
          .ant-table-thead > tr > th {
            padding: 8px 8px !important;
            background-color: ${colors.SECONDARY_300} !important;
          }
          .ant-table-thead > tr > th.ant-table-column-sort {
            background: var(--Semantics-Info-info-500, #225a9c) !important;
            mix-blend-mode: color-dodge;
          }
          .ant-table-tbody > tr:nth-child(even) {
            background-color: ${colors.SECONDARY_400};
          }
          .ant-table-tbody > tr:nth-child(odd) {
            background-color: ${colors.SECONDARY_300};
          }
          .ant-table-tbody > tr > td {
            padding: 8px 8px !important;
            vertical-align: middle;
          }
          .ant-table-tbody > tr > td.ant-table-column-sort {
            background: var(--Semantics-Info-info-500, #225a9c) !important;
            mix-blend-mode: color-dodge;
          }
          @media (max-width: 768px) {
            padding: 0 16px !important;
          }
          .ant-table-tbody
            > tr:nth-child(even)
            > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
            background-color: ${colors.SECONDARY_400} !important;
          }
          .ant-table-tbody
            > tr:nth-child(odd)
            > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
            background-color: ${colors.SECONDARY_300} !important;
          }
          .ant-table-tbody > tr:nth-child(even) > td:nth-child(1) {
            background-color: ${colors.SECONDARY_400} !important;
          }
          .ant-table-tbody > tr:nth-child(odd) > td:nth-child(1) {
            background-color: ${colors.SECONDARY_300} !important;
          }
        `}
      />
    )
  }, [data, loading, columns, statIdByAbbr, currentSortAbbr, direction])

  return (
    <>
      {/* <ReprocessingOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
      /> */}
      <ReprocessingSettingsOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
      />
      <div
        css={css`
          margin-top: 24px;

          ${scheduleReprocessingListener &&
          css`
            opacity: 0.7;
            filter: blur(2px);
            pointer-events: none;
          `}
        `}
      >
        <GameListFilters
          filters={filters}
          team_id={entityId}
          setSlug={setSlug}
          state={state}
          setState={setState}
          loading={loading}
          teams={teams}
          team={team}
          orgId={orgId}
          scheduleId={entityId}
          onTeamChange={onTeamChange}
          onScheduleChange={onScheduleChange}
          isHidden={areFiltersHidden}
        >
          <Flex
            column
            noFlex
            css={css`
              margin-right: 16px;
              .ant-btn-default {
                color: rgba(0, 0, 0, 0.88) !important;
                background-color: transparent !important;
              }

              button {
                text-transform: capitalize !important;
              }
            `}
          >
            <label
              htmlFor={isGoalieStats ? 'skater-stats' : 'goalie-stats'}
              css={css`
                height: 12px;
                font-size: 12px;
                line-height: 12px;
              `}
            >
              Player Type
            </label>
            <Flex
              css={css`
                display: flex;
                margin-top: 8px;
                gap: 8px;
              `}
            >
              <SortOrderButton
                disabled={loading}
                type={!isGoalieStats ? 'primary' : 'default'}
                className={cn({ sorted: !isGoalieStats })}
                onClick={() => {
                  setIsGoalieStats(false)
                  setDirection(getDefaultDirection(playerDefaultSort))
                  setSortBy(playerDefaultSort)
                }}
              >
                {isSoccer ? 'Player' : 'Skater'}
              </SortOrderButton>
              <SortOrderButton
                disabled={loading}
                type={isGoalieStats ? 'primary' : 'default'}
                className={cn({ sorted: isGoalieStats })}
                onClick={() => {
                  setIsGoalieStats(true)
                  setDirection(getDefaultDirection(goalieDefaultSort))
                  setSortBy(goalieDefaultSort)
                }}
              >
                {isSoccer ? 'Keeper' : 'Goalie'}
              </SortOrderButton>
            </Flex>
          </Flex>
          {isSchedule && scheduleForSearch ? (
            <Flex column noFlex>
              <label
                htmlFor={isGoalieStats ? 'skater-stats' : 'goalie-stats'}
                css={css`
                  height: 12px;
                  font-size: 12px;
                  line-height: 12px;
                `}
              >
                Stats Filter
              </label>
              <Flex
                css={css`
                  margin-top: 8px;
                  padding: 0 16px 16px 0;
                `}
              >
                <SortOrderButton
                  disabled={loading}
                  type={isGlobalFilterEnabled ? 'primary' : 'default'}
                  onClick={() => {
                    setIsGlobalFilterEnabled(true)
                    setSlug(BASE_SLUG)
                  }}
                  className={cn({ sorted: isGlobalFilterEnabled })}
                >
                  {'Season'}
                </SortOrderButton>
                <SortOrderButton
                  disabled={loading}
                  type={!isGlobalFilterEnabled ? 'primary' : 'default'}
                  onClick={() => {
                    setIsGlobalFilterEnabled(false)
                    if (scheduleForSearch && isSchedule) {
                      setSlug(`/schedules/${scheduleForSearch}/stats/players`)
                    }
                  }}
                  css={css`
                    margin-left: 8px;
                  `}
                  className={cn({ sorted: !isGlobalFilterEnabled })}
                >
                  {'Division'}
                </SortOrderButton>
              </Flex>
            </Flex>
          ) : null}
        </GameListFilters>
        {error ? (
          <div
            css={css`
              margin-top: 100px;
              text-align: center;
              display: flex;
              flex-direction: column;
              gap: 12px;
              align-items: center;
            `}
          >
            <Typography.Text type='danger'>{String(error)}</Typography.Text>
            <Button
              text='Refresh'
              variant='primary'
              onClick={() => getItems(1, undefined, 'reset')}
              isLoading={loading}
              disabled={loading}
            />
          </div>
        ) : (
          <div>
            {displaySearch ? (
              <div
                css={css`
                  margin-bottom: 16px;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <Typography.Text strong>Search Player:</Typography.Text>
                <div>
                  <Input.Search
                    value={searchValue}
                    placeholder='Enter player name'
                    onChange={(e) => {
                      setSearchValue(e.target.value)
                    }}
                    suffix={
                      <button
                        disabled={searchValue === ''}
                        onClick={(e) => {
                          e.preventDefault()
                          setSearchValue('')
                          getItems(1)
                        }}
                        css={css`
                          background: none;
                          border: none;
                          cursor: pointer;
                          opacity: ${searchValue === '' ? 0 : 1};
                        `}
                      >
                        <Icon name='times' />
                      </button>
                    }
                    onSearch={() => getItems(1)}
                    loading={loading}
                    disabled={loading}
                  />
                </div>
              </div>
            ) : null}
            {renderTable()}

            {/* Infinite scroll footer */}
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 24px 0;
                min-height: 60px;
              `}
            >
              {isAppending && (
                <Flex
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                  `}
                >
                  <Spin size='small' />
                  <Typography.Text
                    css={css`
                      color: ${colors.NEUTRAL_50};
                      font-size: 14px;
                      line-height: 1;
                    `}
                  >
                    Loading More...
                  </Typography.Text>
                </Flex>
              )}
              {!hasMore && !loading && items.length > 0 && (
                <Typography.Text
                  css={css`
                    color: ${colors.NEUTRAL_50};
                    font-size: 14px;
                  `}
                >
                  No More Results
                </Typography.Text>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    statTypes: getStatTypesBySportId(state, sportId),
  }
}

const mapDispatchToProps = (_) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(Statistics)
